<template>
  <div class="campaign-scheduler" v-if="showScheduler">
    <header class="scheduler-header">
      <h3>Schedule Your Campaign</h3>
    </header>

    <div class="scheduler-body">
      <div class="timezone-row">
        <label class="timezone-field" for="campaign-timezone">
          <span class="field__label">First select the campaign time zone</span>
          <select id="campaign-timezone" v-model="selectedTimezone" class="timezone-select" @change="updateSchedule">
            <option v-for="timezone in timezoneOptions" :key="timezone" :value="timezone">
              {{ timezone }}
            </option>
          </select>
        </label>
      </div>

      <div class="action-stack" role="group" aria-label="Schedule selection">
        <button
          type="button"
          class="schedule-btn schedule-btn--primary"
          aria-label="Set start date and time"
          @click="openStartModal"
        >
          <span class="schedule-btn__label">Start</span>
          <span class="schedule-btn__value">{{ startButtonStateLabel }}</span>
        </button>

        <button
          type="button"
          class="schedule-btn"
          :disabled="!selectedDate"
          :aria-disabled="!selectedDate"
          aria-label="Set end date and time"
          @click="openEndModal"
        >
          <span class="schedule-btn__label">End</span>
          <span class="schedule-btn__value">{{ endButtonStateLabel }}</span>
        </button>
      </div>
      <div class="schedule-visual-stack" aria-live="polite">
        <div class="calendar-pair-shell">
          <div class="calendar-pair-toolbar">
            <button
              type="button"
              class="calendar-nav-btn"
              aria-label="Previous month"
              @click="goToPrevMonth"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" style="width:16px;height:16px;display:block;flex:none;">
                <path d="M10 13L5 8l5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button
              type="button"
              class="calendar-today-btn"
              :disabled="isOnAnchorMonth"
              :aria-disabled="isOnAnchorMonth"
              aria-label="Jump back to selected start month"
              @click="goToAnchorMonth"
            >
              Today
            </button>
            <button
              type="button"
              class="calendar-nav-btn"
              aria-label="Next month"
              @click="goToNextMonth"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" style="width:16px;height:16px;display:block;flex:none;">
                <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="calendar-card-pair">
            <section class="calendar-card" aria-label="Calendar — primary month">
              <header class="calendar-card__header">
                <i class="fas fa-calendar-alt" aria-hidden="true"></i>
                <h4>{{ calendarMonthLabel }}</h4>
              </header>
              <div class="calendar-weekdays" aria-hidden="true">
                <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
              </div>
              <div class="calendar-grid" role="grid" aria-label="Campaign date range — primary month">
                <div
                  v-for="day in calendarDays"
                  :key="day.key"
                  class="calendar-day"
                  :class="{
                    'is-outside': !day.inCurrentMonth,
                    'is-in-range': day.inRange,
                    'is-range-start': day.rangeStart,
                    'is-range-end': day.rangeEnd,
                    'is-start': day.isStart,
                    'is-end': day.isEnd,
                    'is-today': day.isToday
                  }"
                  role="gridcell"
                  :aria-selected="day.isStart || day.isEnd"
                >
                  <span>{{ day.dayNumber }}</span>
                </div>
              </div>
            </section>

            <section class="calendar-card calendar-card--secondary" aria-label="Calendar — next month">
              <header class="calendar-card__header">
                <i class="fas fa-calendar-alt" aria-hidden="true"></i>
                <h4>{{ nextCalendarMonthLabel }}</h4>
              </header>
              <div class="calendar-weekdays" aria-hidden="true">
                <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
              </div>
              <div class="calendar-grid" role="grid" aria-label="Campaign date range — next month">
                <div
                  v-for="day in nextCalendarDays"
                  :key="day.key"
                  class="calendar-day"
                  :class="{
                    'is-outside': !day.inCurrentMonth,
                    'is-in-range': day.inRange,
                    'is-range-start': day.rangeStart,
                    'is-range-end': day.rangeEnd,
                    'is-start': day.isStart,
                    'is-end': day.isEnd,
                    'is-today': day.isToday
                  }"
                  role="gridcell"
                  :aria-selected="day.isStart || day.isEnd"
                >
                  <span>{{ day.dayNumber }}</span>
                </div>
              </div>
            </section>
          </div>
        </div>

        <section class="schedule-summary-card" aria-label="Schedule summary">
          <p v-if="durationHighlight" class="schedule-duration-highlight">{{ durationHighlight }}</p>
          <p v-if="bothPastRangeError" class="schedule-summary-error">{{ bothPastRangeError }}</p>
          <p class="schedule-summary-copy">{{ durationSummary }}</p>
        </section>
      </div>
    </div>

    <div
      v-if="isStartModalOpen"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Set start date and time"
      @click.self="closeStartModal"
    >
      <div class="modal-container">
        <header class="modal-header">
          <button
            type="button"
            class="modal-close-button"
            @click="closeStartModal"
            aria-label="Close start modal"
          >
            &times;
          </button>
          <p class="modal-card__title">Pick your launch date then set the time</p>
        </header>
        <div class="modal-body">
          <div class="modal-grid">
            <div class="field">
              <span class="field__label">Start date</span>
              <div class="picker-container">
                <button type="button" class="picker-btn" @click="openDatePicker('startDateInput')">
                  <i class="fas fa-calendar-alt"></i>
                  <span class="picker-btn__value">{{ displayDate || 'Select start date' }}</span>
                </button>
                <input
                  type="date"
                  ref="startDateInput"
                  v-model="selectedDate"
                  :min="minDate"
                  @change="updateSchedule"
                  class="hidden-input"
                  aria-label="Start date"
                />
              </div>
              <small v-if="validation.campaignStartDate?.error" class="field__help field__help--error">
                {{ validation.campaignStartDate.error }}
              </small>
            </div>
            <div class="field">
              <span class="field__label">Start time</span>
              <div class="picker-container">
                <button type="button" class="picker-btn" @click="openDatePicker('startTimeInput')">
                  <i class="fas fa-clock"></i>
                  <span class="picker-btn__value">{{ displayTime || 'Select start time' }}</span>
                </button>
                <input
                  type="time"
                  ref="startTimeInput"
                  v-model="selectedTime"
                  @change="updateSchedule"
                  class="hidden-input"
                  aria-label="Start time"
                />
              </div>
              <small v-if="validation.campaignStartTime?.error" class="field__help field__help--error">
                {{ validation.campaignStartTime.error }}
              </small>
            </div>
          </div>
        </div>
        <footer class="modal-footer">
          <button type="button" class="ghost-btn" @click="closeStartModal" style="margin-right: 10px;">Cancel</button>
          <button type="button" class="primary-btn" @click="saveStartModal">Done</button>
        </footer>
      </div>
    </div>

    <div
      v-if="isEndModalOpen"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Set end date and time"
      @click.self="closeEndModal"
    >
      <div class="modal-container">
        <header class="modal-header">
          <button
            type="button"
            class="modal-close-button"
            @click="closeEndModal"
            aria-label="Close end modal"
          >
            &times;
          </button>
          <p class="modal-card__title">Choose your end date then set the time</p>
        </header>
        <div class="modal-body">
          <div class="modal-grid">
            <div class="field">
              <span class="field__label">End date</span>
              <div class="picker-container">
                <button 
                  type="button" 
                  class="picker-btn" 
                  :disabled="!selectedDate"
                  @click="openDatePicker('endDateInput')"
                >
                  <i class="fas fa-calendar-alt"></i>
                  <span class="picker-btn__value">{{ displayEndDate || 'Select end date' }}</span>
                </button>
                <input
                  type="date"
                  ref="endDateInput"
                  v-model="selectedEndDate"
                  :min="selectedDate || minDate"
                  :disabled="!selectedDate"
                  @change="updateSchedule"
                  class="hidden-input"
                  aria-label="End date"
                />
              </div>
              <small v-if="validation.campaignEndDate?.error" class="field__help field__help--error">
                {{ validation.campaignEndDate.error }}
              </small>
            </div>
            <div class="field">
              <span class="field__label">End time</span>
              <div class="picker-container">
                <button 
                  type="button" 
                  class="picker-btn" 
                  :disabled="!selectedDate"
                  @click="openDatePicker('endTimeInput')"
                >
                  <i class="fas fa-clock"></i>
                  <span class="picker-btn__value">{{ displayEndTime || 'Select end time' }}</span>
                </button>
                <input
                  type="time"
                  ref="endTimeInput"
                  v-model="selectedEndTime"
                  :disabled="!selectedDate"
                  @change="updateSchedule"
                  class="hidden-input"
                  aria-label="End time"
                />
              </div>
              <small v-if="validation.campaignEndTime?.error" class="field__help field__help--error">
                {{ validation.campaignEndTime.error }}
              </small>
            </div>
          </div>
        </div>
        <footer class="modal-footer">
          <button type="button" class="ghost-btn" @click="closeEndModal" style="margin-right: 10px;">Cancel</button>
          <button type="button" class="primary-btn" @click="saveEndModal" :disabled="!selectedDate">Done</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, onUnmounted } from 'vue'
import { useUIStore } from '@/store/ui.store'
import { combineLocalDateTimeInTimeZone, getTimeZoneOptions, normalizeTimeZone } from '@/utils/timezone'

export interface CampaignSchedule {
  startTime: string | null
  startDate: string | null
  startDateTime: Date | null
  endTime: string | null
  endDate: string | null
  endDateTime: Date | null
  timezone?: string | null
}

export default defineComponent({
  name: 'CampaignScheduler',
  props: {
    showScheduler: {
      type: Boolean,
      default: false
    },
    validation: {
      type: Object,
      default: () => ({
        campaignStartDate: { error: '', success: '' },
        campaignStartTime: { error: '', success: '' },
        campaignEndDate: { error: '', success: '' },
        campaignEndTime: { error: '', success: '' }
      })
    },
    initialSchedule: {
      type: Object as () => CampaignSchedule | null,
      default: null
    }
  },
  emits: ['schedule-change'],
  setup(props, { emit }) {
    const uiStore = useUIStore()
    const selectedDate = ref('')
    const selectedTime = ref('')
    const selectedEndDate = ref('')
    const selectedEndTime = ref('')
    const getDeviceTimezone = () => normalizeTimeZone(
      typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC'
    )

    const resolveScheduleTimezone = (timezone?: string | null) => (
      timezone ? normalizeTimeZone(timezone) : getDeviceTimezone()
    )

    const selectedTimezone = ref(getDeviceTimezone())
    const isStartModalOpen = ref(false)
    const isEndModalOpen = ref(false)

    const startDateInput = ref<HTMLInputElement | null>(null)
    const startTimeInput = ref<HTMLInputElement | null>(null)
    const endDateInput = ref<HTMLInputElement | null>(null)
    const endTimeInput = ref<HTMLInputElement | null>(null)
    const defaultStartDate = ref('')
    const defaultStartTime = ref('')
    const defaultEndDate = ref('')
    const defaultEndTime = ref('')
    const timezoneOptions = getTimeZoneOptions()

    const inputs = {
      startDateInput,
      startTimeInput,
      endDateInput,
      endTimeInput
    }

    const toDateValue = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const toTimeValue = (date: Date) => {
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    }

    const setDefaultScheduleRange = () => {
      const now = new Date()
      const defaultStart = new Date(now)
      const defaultEnd = new Date(defaultStart)
      defaultEnd.setDate(defaultEnd.getDate() + 28)

      defaultStartDate.value = toDateValue(defaultStart)
      defaultStartTime.value = toTimeValue(defaultStart)
      defaultEndDate.value = toDateValue(defaultEnd)
      defaultEndTime.value = toTimeValue(defaultEnd)

      selectedDate.value = defaultStartDate.value
      selectedTime.value = defaultStartTime.value
      selectedEndDate.value = defaultEndDate.value
      selectedEndTime.value = defaultEndTime.value
    }

    const updateSchedule = () => {
      const schedule: CampaignSchedule = {
        startDate: selectedDate.value || null,
        startTime: selectedTime.value || null,
        startDateTime: combineLocalDateTimeInTimeZone(selectedDate.value, selectedTime.value, selectedTimezone.value),
        endDate: selectedEndDate.value || null,
        endTime: selectedEndTime.value || null,
        endDateTime: combineLocalDateTimeInTimeZone(selectedEndDate.value, selectedEndTime.value, selectedTimezone.value),
        timezone: selectedTimezone.value,
      }
      
      emit('schedule-change', schedule)
    }

    const isFirstSync = ref(true)

    watch(() => props.initialSchedule, (newSchedule) => {
      if (newSchedule) {
        const sessionKey = 'campaign-scheduler-now-refreshed'
        const alreadyRefreshed = sessionStorage.getItem(sessionKey)

        if (isFirstSync.value && !alreadyRefreshed) {
          setDefaultScheduleRange()
          selectedTimezone.value = resolveScheduleTimezone(newSchedule.timezone)
          sessionStorage.setItem(sessionKey, 'true')
          isFirstSync.value = false
          updateSchedule()
        } else {
          if (!defaultStartDate.value || !defaultStartTime.value || !defaultEndDate.value || !defaultEndTime.value) {
            setDefaultScheduleRange()
          }

          selectedTimezone.value = resolveScheduleTimezone(newSchedule.timezone)

          if (newSchedule.startDate) {
            selectedDate.value = newSchedule.startDate
          }
          if (newSchedule.startTime) {
            selectedTime.value = newSchedule.startTime
          }
          isFirstSync.value = false
        }

        if (newSchedule.endDate) {
          selectedEndDate.value = newSchedule.endDate
        }
        if (newSchedule.endTime) {
          selectedEndTime.value = newSchedule.endTime
        }
      } else if (!defaultStartDate.value || !defaultStartTime.value || !defaultEndDate.value || !defaultEndTime.value) {
        setDefaultScheduleRange()
        selectedTimezone.value = getDeviceTimezone()
        updateSchedule()
      }
    }, { immediate: true, deep: true })

    watch([isStartModalOpen, isEndModalOpen], ([startOpen, endOpen], [oldStartOpen, oldEndOpen]) => {
      const wasOpen = oldStartOpen || oldEndOpen
      const isOpen = startOpen || endOpen
      
      if (isOpen && !wasOpen) {
        uiStore.openModal()
      } else if (!isOpen && wasOpen) {
        uiStore.closeModal()
      }
    })

    onUnmounted(() => {
      if (isStartModalOpen.value || isEndModalOpen.value) {
        uiStore.closeModal()
      }
    })
    
    const minDate = computed(() => {
      const today = new Date()
      return today.toISOString().split('T')[0]
    })

    const displayDate = computed(() => {
      if (!selectedDate.value) return ''
      const date = new Date(selectedDate.value)
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    })

    const displayTime = computed(() => {
      if (!selectedTime.value) return ''
      return selectedTime.value
    })

    const displayEndDate = computed(() => {
      if (!selectedEndDate.value) return ''
      const date = new Date(selectedEndDate.value)
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    })

    const displayEndTime = computed(() => {
      if (!selectedEndTime.value) return ''
      return selectedEndTime.value
    })

    const formatDateTimeForTimezone = (date: Date, options?: Intl.DateTimeFormatOptions) =>
      date.toLocaleString('en-GB', {
        timeZone: selectedTimezone.value,
        ...options,
      })

    const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    const parseDateString = (value: string): Date | null => {
      if (!value) return null
      const [year, month, day] = value.split('-').map(Number)
      if (!year || !month || !day) return null
      return new Date(year, month - 1, day)
    }

    const normalizeDate = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

    const toDateKey = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const selectedStartDateObj = computed(() => parseDateString(selectedDate.value))
    const selectedEndDateObj = computed(() => parseDateString(selectedEndDate.value))

    const rangeStartDate = computed(() => {
      const start = selectedStartDateObj.value
      const end = selectedEndDateObj.value
      if (start && end && end < start) return normalizeDate(end)
      return start ? normalizeDate(start) : null
    })

    const rangeEndDate = computed(() => {
      const start = selectedStartDateObj.value
      const end = selectedEndDateObj.value
      if (start && end && end < start) return normalizeDate(start)
      return end ? normalizeDate(end) : null
    })

    const viewedMonthOffset = ref(0)

    const anchorMonthDate = computed(() => {
      const base = selectedStartDateObj.value
        ? normalizeDate(selectedStartDateObj.value)
        : normalizeDate(new Date())
      return new Date(base.getFullYear(), base.getMonth(), 1)
    })

    const primaryMonthDate = computed(() => {
      const anchor = anchorMonthDate.value
      return new Date(anchor.getFullYear(), anchor.getMonth() + viewedMonthOffset.value, 1)
    })

    const secondaryMonthDate = computed(() => {
      const primary = primaryMonthDate.value
      return new Date(primary.getFullYear(), primary.getMonth() + 1, 1)
    })

    const calendarBaseDate = computed(() => primaryMonthDate.value)

    const formatMonthLabel = (date: Date) => date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    })

    const calendarMonthLabel = computed(() => formatMonthLabel(primaryMonthDate.value))
    const nextCalendarMonthLabel = computed(() => formatMonthLabel(secondaryMonthDate.value))

    const todayDateKey = computed(() => toDateKey(normalizeDate(new Date())))

    const buildMonthDays = (monthDate: Date) => {
      const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
      const gridStart = new Date(firstOfMonth)
      gridStart.setDate(firstOfMonth.getDate() - firstOfMonth.getDay())

      const start = rangeStartDate.value
      const end = rangeEndDate.value

      const raw = [] as Array<{
        key: string
        dayNumber: number
        inCurrentMonth: boolean
        isStart: boolean
        isEnd: boolean
        inRange: boolean
        isWeekStart: boolean
        isWeekEnd: boolean
        isToday: boolean
      }>

      for (let index = 0; index < 42; index += 1) {
        const date = new Date(gridStart)
        date.setDate(gridStart.getDate() + index)
        const normalizedDate = normalizeDate(date)
        const dateKey = toDateKey(normalizedDate)
        const inCurrentMonth = normalizedDate.getMonth() === monthDate.getMonth()
        const isStart = inCurrentMonth && selectedDate.value === dateKey
        const isEnd = inCurrentMonth && selectedEndDate.value === dateKey
        const inRange = Boolean(start && end && normalizedDate >= start && normalizedDate <= end)
        const isWeekStart = normalizedDate.getDay() === 0
        const isWeekEnd = normalizedDate.getDay() === 6

        raw.push({
          key: `${dateKey}-${index}`,
          dayNumber: normalizedDate.getDate(),
          inCurrentMonth,
          isStart,
          isEnd,
          inRange,
          isWeekStart,
          isWeekEnd,
          isToday: inCurrentMonth && todayDateKey.value === dateKey
        })
      }

      return raw.map((day, index) => {
        const prev = raw[index - 1]
        const next = raw[index + 1]
        const isChunkStart = day.inRange && (!prev || !prev.inRange || day.isWeekStart)
        const isChunkEnd = day.inRange && (!next || !next.inRange || day.isWeekEnd)
        return {
          key: day.key,
          dayNumber: day.dayNumber,
          inCurrentMonth: day.inCurrentMonth,
          isStart: day.isStart,
          isEnd: day.isEnd,
          inRange: day.inRange,
          rangeStart: isChunkStart,
          rangeEnd: isChunkEnd,
          isToday: day.isToday
        }
      })
    }

    const calendarDays = computed(() => buildMonthDays(primaryMonthDate.value))
    const nextCalendarDays = computed(() => buildMonthDays(secondaryMonthDate.value))

    const goToPrevMonth = () => { viewedMonthOffset.value -= 1 }
    const goToNextMonth = () => { viewedMonthOffset.value += 1 }
    const goToAnchorMonth = () => { viewedMonthOffset.value = 0 }
    const isOnAnchorMonth = computed(() => viewedMonthOffset.value === 0)

    watch([selectedDate, selectedEndDate], () => { viewedMonthOffset.value = 0 })

    const isStartStillDefault = computed(() => {
      return selectedDate.value === defaultStartDate.value && selectedTime.value === defaultStartTime.value
    })

    const isEndStillDefault = computed(() => {
      return selectedEndDate.value === defaultEndDate.value && selectedEndTime.value === defaultEndTime.value
    })

    const startButtonStateLabel = computed(() => {
      if (!selectedDate.value || !selectedTime.value || isStartStillDefault.value) {
        return 'Now'
      }
      return 'Set, done'
    })

    const endButtonStateLabel = computed(() => {
      if (!selectedEndDate.value || !selectedEndTime.value || isEndStillDefault.value) {
        return '28 days'
      }
      return 'Set, done'
    })

    const bothPastRangeError = computed(() => {
      if (!selectedDate.value || !selectedTime.value || !selectedEndDate.value || !selectedEndTime.value) {
        return ''
      }

      const startDateTime = combineLocalDateTimeInTimeZone(selectedDate.value, selectedTime.value, selectedTimezone.value)
      const endDateTime = combineLocalDateTimeInTimeZone(selectedEndDate.value, selectedEndTime.value, selectedTimezone.value)
      if (!startDateTime || !endDateTime) return ''
      const now = Date.now()

      if (startDateTime.getTime() < now && endDateTime.getTime() < now) {
        return 'Start and end are both in the past. Please set at least one future date.'
      }

      return ''
    })

    const durationSummary = computed(() => {
      if (!selectedDate.value || !selectedTime.value) {
        return 'Choose a start date and time to preview your campaign schedule.'
      }

      const startDateTime = combineLocalDateTimeInTimeZone(selectedDate.value, selectedTime.value, selectedTimezone.value)
      if (!startDateTime) {
        return 'Choose a start date and time to preview your campaign schedule.'
      }
      if (!selectedEndDate.value || !selectedEndTime.value) {
        return `Starts ${formatDateTimeForTimezone(startDateTime, {
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })} ${selectedTimezone.value}. Add an end date and time to complete the range.`
      }

      const endDateTime = combineLocalDateTimeInTimeZone(selectedEndDate.value, selectedEndTime.value, selectedTimezone.value)
      if (!endDateTime) {
        return 'Add an end date and time to complete the range.'
      }
      const durationMs = endDateTime.getTime() - startDateTime.getTime()
      const durationDays = Math.max(1, Math.ceil(durationMs / (1000 * 60 * 60 * 24)))

      if (durationMs < 0) {
        return 'End time must be after start time. Please adjust your end date or time.'
      }

      return `Your experience will run for ${durationDays} day${durationDays === 1 ? '' : 's'} from ${formatDateTimeForTimezone(startDateTime, {
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })} to ${formatDateTimeForTimezone(endDateTime, {
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })} ${selectedTimezone.value}.`
    })

    const durationHighlight = computed(() => {
      if (!selectedDate.value || !selectedTime.value || !selectedEndDate.value || !selectedEndTime.value) {
        return ''
      }

      const startDateTime = combineLocalDateTimeInTimeZone(selectedDate.value, selectedTime.value, selectedTimezone.value)
      const endDateTime = combineLocalDateTimeInTimeZone(selectedEndDate.value, selectedEndTime.value, selectedTimezone.value)
      if (!startDateTime || !endDateTime) {
        return ''
      }
      const durationMs = endDateTime.getTime() - startDateTime.getTime()
      if (durationMs < 0) {
        return ''
      }

      const durationDays = Math.max(1, Math.ceil(durationMs / (1000 * 60 * 60 * 24)))
      return `${durationDays} day${durationDays === 1 ? '' : 's'} duration`
    })
    
    const formattedDateTime = computed(() => {
      if (!selectedDate.value || !selectedTime.value) return null
      const dateTime = combineLocalDateTimeInTimeZone(selectedDate.value, selectedTime.value, selectedTimezone.value)
      if (!dateTime) return null
      return dateTime.toLocaleString('en-GB', {
        timeZone: selectedTimezone.value,
        weekday: 'short',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    })

    const formattedEndDateTime = computed(() => {
      if (!selectedEndDate.value || !selectedEndTime.value) return null
      const dateTime = combineLocalDateTimeInTimeZone(selectedEndDate.value, selectedEndTime.value, selectedTimezone.value)
      if (!dateTime) return null
      return dateTime.toLocaleString('en-GB', {
        timeZone: selectedTimezone.value,
        weekday: 'short',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    })

    const launchSummary = computed(() => {
      if (formattedDateTime.value) return formattedDateTime.value
      return 'Select a start date and time'
    })

    const endSummary = computed(() => {
      if (formattedEndDateTime.value) return formattedEndDateTime.value
      if (!selectedDate.value) return 'Add start date to enable end date'
      return 'Select an end date and time'
    })

    const openStartModal = () => {
      isStartModalOpen.value = true
    }

    const openEndModal = () => {
      if (!selectedDate.value) return
      isEndModalOpen.value = true
    }

    const closeStartModal = () => {
      isStartModalOpen.value = false
    }

    const closeEndModal = () => {
      isEndModalOpen.value = false
    }

    const saveStartModal = () => {
      updateSchedule()
      closeStartModal()
    }

    const saveEndModal = () => {
      updateSchedule()
      closeEndModal()
    }

    const openDatePicker = (inputRef: keyof typeof inputs) => {
      const input = inputs[inputRef].value
      if (input) {
        if ('showPicker' in HTMLInputElement.prototype) {
          try {
            input.showPicker()
          } catch (e) {
            console.error('Error showing date picker:', e)
            input.click()
          }
        } else {
          input.click()
        }
      }
    }
    
    watch(() => selectedDate.value, (newDate) => {
      let needsUpdate = false
      if (newDate && !selectedTime.value) {
        const now = new Date()
        const nextHour = new Date(now.getTime() + 60 * 60 * 1000)
        const hours = nextHour.getHours().toString().padStart(2, '0')
        const minutes = '00'
        selectedTime.value = `${hours}:${minutes}`
        needsUpdate = true
      }

      if (!newDate && selectedEndDate.value) {
        selectedEndDate.value = ''
        selectedEndTime.value = ''
        needsUpdate = true
      }
      
      if (needsUpdate) {
        updateSchedule()
      }
    })

    watch(() => selectedEndDate.value, (newDate) => {
      if (newDate && !selectedEndTime.value) {
        if (selectedDate.value === selectedEndDate.value && selectedTime.value) {
          const startTime = new Date(`${selectedDate.value}T${selectedTime.value}`);
          const nextHour = new Date(startTime.getTime() + 60 * 60 * 1000);
          const hours = nextHour.getHours().toString().padStart(2, '0');
          const minutes = '00';
          selectedEndTime.value = `${hours}:${minutes}`;
        } else {
          const now = new Date()
          const nextHour = new Date(now.getTime() + 60 * 60 * 1000)
          const hours = nextHour.getHours().toString().padStart(2, '0')
          const minutes = '00'
          selectedEndTime.value = `${hours}:${minutes}`
        }
        updateSchedule()
      }
    })
    
    return {
      selectedDate,
      selectedTime,
      selectedEndDate,
      selectedEndTime,
      selectedTimezone,
      timezoneOptions,
      minDate,
      isStartModalOpen,
      isEndModalOpen,
      openStartModal,
      closeStartModal,
      saveStartModal,
      openEndModal,
      closeEndModal,
      saveEndModal,
      formattedDateTime,
      formattedEndDateTime,
      launchSummary,
      endSummary,
      updateSchedule,
      startDateInput,
      startTimeInput,
      endDateInput,
      endTimeInput,
      openDatePicker,
      displayDate,
      displayTime,
      displayEndDate,
      displayEndTime,
      weekdayLabels,
      calendarMonthLabel,
      nextCalendarMonthLabel,
      calendarDays,
      nextCalendarDays,
      goToPrevMonth,
      goToNextMonth,
      goToAnchorMonth,
      isOnAnchorMonth,
      durationSummary,
      durationHighlight,
      startButtonStateLabel,
      endButtonStateLabel,
      bothPastRangeError
    }
  }
})
</script>

<style scoped>
.campaign-scheduler {
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: var(--ui-inverse-font-family);
}

.scheduler-header {
  text-align: center;
  max-width: 560px;
  margin: 0 auto;
}

.scheduler-header h3 {
  margin: 0 0 4px 0;
  color: #ffffff;
  font-size: clamp(1.5rem, 2vw, 2rem);
  font-weight: 800;
  font-family: var(--ui-inverse-font-family);
}

.scheduler-header p {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.95rem;
  font-family: var(--ui-inverse-font-family);
}

.scheduler-body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;
  max-width: var(--base-layout-schedule-form-max-width);
}

.timezone-row {
  display: flex;
  justify-content: center;
}

.timezone-field {
  display: grid;
  gap: 0.4rem;
  width: min(100%, 18rem);
  margin-inline: auto;
}

.timezone-select {
  min-height: 2.5rem;
  border-radius: 50px;
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
  padding: 0 0.85rem;
}

.action-stack {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.schedule-visual-stack {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.calendar-pair-shell {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.calendar-pair-toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.calendar-nav-btn {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(45, 49, 73, 0.7);
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
  font-size: 0.95rem;
}

.calendar-nav-btn:hover {
  background: rgba(75, 81, 110, 0.85);
  border-color: rgba(255, 255, 255, 0.35);
}

.calendar-nav-btn:active {
  transform: scale(0.96);
}

.calendar-nav-btn svg {
  width: 16px;
  height: 16px;
  display: block;
  stroke: #ffffff;
  fill: none;
}

.calendar-nav-btn svg path {
  stroke: #ffffff;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
}

.calendar-today-btn {
  min-height: 36px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(45, 49, 73, 0.7);
  color: #ffffff;
  cursor: pointer;
  font-weight: 600;
  letter-spacing: 0.02em;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.calendar-today-btn:hover:not(:disabled) {
  background: rgba(75, 81, 110, 0.85);
  border-color: rgba(255, 255, 255, 0.35);
}

.calendar-today-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.calendar-card-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.calendar-card-pair .calendar-card {
  padding: 0.75rem 0.85rem;
}

.calendar-card-pair .calendar-card__header {
  margin-bottom: 8px;
}

.calendar-card-pair .calendar-card__header h4 {
  font-size: 1rem;
}

.calendar-card-pair .calendar-weekdays {
  margin-bottom: 4px;
}

.calendar-card-pair .calendar-day {
  min-height: 28px;
  font-size: 0.85rem;
}

/* Mobile + short-height desktop: show ONE calendar at a time. The
   wizard forbids in-section scrolling, so dual-month vertical stack
   would clip. Users browse months via the prev/next chevrons; the
   summary card communicates the full range textually. */
@media (max-width: 767px), (max-height: 820px) {
  .calendar-card-pair {
    grid-template-columns: 1fr;
  }
  .calendar-card--secondary {
    display: none;
  }
}

.calendar-card,
.schedule-summary-card {
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #2d3149;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.24);
}

.calendar-card {
  padding: clamp(1rem, 2.3vw, 1.5rem);
}

.calendar-card__header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 14px;
}

.calendar-card__header i {
  color: rgba(255, 255, 255, 0.8);
}

.calendar-card__header h4 {
  margin: 0;
  color: var(--color-heading);
  font-size: clamp(1.1rem, 2vw, 1.5rem);
  font-weight: 800;
  font-family: var(--ui-inverse-font-family);
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
  margin-bottom: 8px;
}

.calendar-weekdays span {
  text-align: center;
  font-size: 0.76rem;
  color: rgba(220, 230, 255, 0.75);
  letter-spacing: 0.03em;
  font-weight: 600;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px 0;
}

.calendar-day {
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cfd6e6;
  font-weight: 600;
  font-size: 0.88rem;
  position: relative;
}

.calendar-day.is-outside {
  color: rgba(200, 210, 230, 0.35);
}

.calendar-day.is-in-range {
  background: rgba(126, 233, 181, 0.45);
  color: #eaf9f1;
}

.calendar-day.is-range-start {
  border-top-left-radius: 999px;
  border-bottom-left-radius: 999px;
}

.calendar-day.is-range-end {
  border-top-right-radius: 999px;
  border-bottom-right-radius: 999px;
}

.calendar-day.is-start,
.calendar-day.is-end {
  color: #1a1f36;
  font-weight: 800;
}

.calendar-day.is-start span,
.calendar-day.is-end span {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: #7ee9b5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.calendar-day.is-today:not(.is-start):not(.is-end) span {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.schedule-summary-card {
  padding: clamp(1rem, 2.2vw, 1.4rem);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.schedule-duration-highlight {
  margin: 0;
  display: inline-flex;
  align-self: center;
  padding: 0.28rem 0.68rem;
  border-radius: 999px;
  background: rgba(126, 233, 181, 0.2);
  border: 1px solid rgba(126, 233, 181, 0.42);
  color: var(--vt-c-accent);
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.schedule-summary-copy {
  margin: 0;
  font-size: 1.04rem;
  line-height: 1.45;
  color: #d5deed;
}

.schedule-summary-error {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.35;
  color: #8f1434;
  font-weight: 700;
}

.schedule-btn {
  width: 100%;
  min-width: 0;
  text-align: left;
  border-radius: 16px;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.78);
  background: var(--vt-c-accent);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #101a34;
  box-shadow: 0 0.45rem 1.1rem rgba(0, 10, 25, 0.14);
  transition: border-color 0.15s ease, transform 0.12s ease, background 0.15s ease;
}

.schedule-btn:hover,
.schedule-btn:focus-visible {
  border-color: var(--vt-c-accent);
  background: #8ef6c6;
  outline: none;
  transform: translateY(-1px);
}

.schedule-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.schedule-btn--primary {
  border-color: var(--vt-c-accent);
  background: var(--vt-c-accent);
}

.schedule-btn__label {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.schedule-btn__value {
  display: block;
  max-width: 100%;
  min-width: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.35;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.schedule-btn__status {
  font-size: 0.9rem;
  font-weight: 600;
}

.schedule-btn__status--error {
  color: #8f1434;
}

.schedule-btn__status--success {
  color: var(--ui-inverse-text);
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(16, 26, 52, 0.12);
  color: var(--ui-inverse-text);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.75rem;
}

.pill--primary {
  background: rgba(16, 26, 52, 0.16);
  color: var(--ui-inverse-text);
}

.modal-card__title {
  margin: 0;
  color: var(--ui-inverse-text);
  font-weight: 700;
  padding-right: 40px; /* Space for close button */
}

.modal-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field__label {
  font-weight: 600;
  color: var(--ui-inverse-text);
  font-size: 0.95rem;
}

.picker-container {
  position: relative;
  width: 100%;
}

.picker-btn {
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--ui-inverse-control-border);
  background: var(--ui-inverse-control-bg);
  color: var(--ui-inverse-control-text);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  height: auto;
  margin-top: 0;
  box-shadow: none;
}

.picker-btn:hover:not(:disabled) {
  border-color: rgba(16, 26, 52, 0.5);
  background: var(--ui-inverse-control-bg-hover);
}

.picker-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.picker-btn i {
  color: var(--ui-inverse-control-text);
  font-size: 1.1rem;
}

.picker-btn__value {
  font-size: 1rem;
  color: var(--ui-inverse-control-text);
}

.hidden-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  border: none;
  visibility: visible;
}

@supports (-webkit-touch-callout: none) {
  .hidden-input {
    pointer-events: auto;
  }
}

.field__help {
  font-size: 0.85rem;
  color: var(--ui-inverse-text-muted);
}

.field__help--error {
  color: #8f1434;
}

.primary-btn,
.ghost-btn {
  padding: 10px 16px;
  border-radius: 12px;
  font-weight: 700;
  border: 1px solid transparent;
  cursor: pointer;
  height: auto;
  margin-top: 0;
  width: auto;
  box-shadow: none;
}

.primary-btn {
  background: var(--vt-c-accent);
  color: #0f1720;
  border-color: var(--vt-c-accent);
}

.ghost-btn {
  background: transparent;
  color: var(--ui-inverse-text);
  border-color: var(--ui-inverse-border-strong);
}

.primary-btn:hover {
  background: var(--vt-c-accent-dark);
  border-color: var(--vt-c-accent-dark);
  transform: none;
}

.ghost-btn:hover {
  background: rgba(16, 26, 52, 0.08);
  border-color: rgba(16, 26, 52, 0.3);
  transform: none;
}


@media (min-width: 1024px) {
  .campaign-scheduler {
    gap: 6px;
  }

  .scheduler-body {
    gap: 16px;
    max-width: min(560px, 100%);
    margin-inline: auto;
  }

  .action-stack {
    gap: 12px;
  }

  .schedule-btn {
    padding: 12px 14px;
  }

  /* Compact calendar for desktop */
  .calendar-card {
    padding: clamp(0.75rem, 1.4vw, 1.1rem);
  }

  .calendar-card__header {
    margin-bottom: 10px;
  }

  .calendar-card__header h4 {
    font-size: clamp(0.95rem, 1.3vw, 1.15rem);
  }

  .calendar-weekdays {
    margin-bottom: 5px;
  }

  .calendar-weekdays span {
    font-size: 0.7rem;
  }

  .calendar-grid {
    gap: 3px 0;
  }

  .calendar-day {
    min-height: 28px;
    font-size: 0.8rem;
  }

  .calendar-day.is-start span,
  .calendar-day.is-end span {
    width: 28px;
    height: 28px;
  }

  .schedule-summary-card {
    padding: clamp(0.75rem, 1.4vw, 1.1rem);
    gap: 0.5rem;
  }

  .schedule-summary-copy {
    font-size: 0.92rem;
    line-height: 1.4;
  }

  .scheduler-header h3 {
    font-size: clamp(1.3rem, 1.8vw, 1.8rem);
    margin-bottom: 2px;
  }
}

@media (max-width: 640px) {
  .campaign-scheduler {
    margin-top: 0;
    gap: 7px;
  }

  .scheduler-header h3 {
    font-size: clamp(1rem, 4.4vw, 1.3rem);
    margin-bottom: 3px;
  }

  .scheduler-header p {
    font-size: 0.82rem;
  }

  .scheduler-body {
    gap: 8px;
  }

  .timezone-field {
    gap: 0.3rem;
  }

  .timezone-select {
    min-height: 2.2rem;
    font-size: 0.85rem;
  }

  .schedule-visual-stack {
    gap: 8px;
  }

  .action-stack {
    gap: 6px;
  }

  .schedule-btn {
    padding: 7px 10px;
    gap: 3px;
  }

  .schedule-btn__label {
    font-size: 0.68rem;
  }

  .schedule-btn__value {
    font-size: 0.8rem;
  }

  .schedule-btn__status {
    font-size: 0.68rem;
    line-height: 1.2;
  }

  .calendar-card {
    padding: 0.55rem;
  }

  .calendar-card__header {
    margin-bottom: 6px;
    gap: 5px;
  }

  .calendar-card__header h4 {
    font-size: clamp(0.85rem, 4vw, 0.98rem);
  }

  .calendar-weekdays {
    margin-bottom: 4px;
  }

  .calendar-weekdays span {
    font-size: 0.58rem;
  }

  .calendar-grid {
    gap: 2px 0;
  }

  .calendar-day {
    min-height: 20px;
    font-size: 0.7rem;
  }

  .calendar-day.is-start span,
  .calendar-day.is-end span {
    width: 20px;
    height: 20px;
  }

  .schedule-summary-card {
    padding: 0.6rem;
    gap: 6px;
  }

  .schedule-summary-copy {
    font-size: 0.8rem;
    line-height: 1.3;
  }

  .schedule-duration-highlight {
    font-size: 0.7rem;
    padding: 0.18rem 0.5rem;
  }
}


/* Short desktop viewports (Mac laptops, small 1080p, etc.) */
@media (min-width: 768px) and (max-height: 820px) {
  .campaign-scheduler {
    gap: 4px;
  }

  .scheduler-header h3 {
    font-size: clamp(1.1rem, 1.6vw, 1.5rem);
    margin-bottom: 0;
  }

  .scheduler-body {
    gap: 8px;
    max-width: min(480px, 100%);
  }

  .schedule-visual-stack {
    gap: 8px;
  }

  .action-stack {
    gap: 8px;
  }

  .schedule-btn {
    padding: 8px 12px;
    border-radius: 12px;
  }

  .calendar-card {
    padding: 0.5rem 0.65rem;
    border-radius: 16px;
  }

  .calendar-card__header {
    margin-bottom: 4px;
  }

  .calendar-card__header h4 {
    font-size: 0.9rem;
  }

  .calendar-weekdays {
    margin-bottom: 2px;
  }

  .calendar-weekdays span {
    font-size: 0.65rem;
  }

  .calendar-grid {
    gap: 1px 0;
  }

  .calendar-day {
    min-height: 22px;
    font-size: 0.75rem;
  }

  .calendar-day.is-start span,
  .calendar-day.is-end span {
    width: 22px;
    height: 22px;
  }

  .schedule-summary-card {
    padding: 0.55rem 0.65rem;
    gap: 0.35rem;
    border-radius: 16px;
  }

  .schedule-summary-copy {
    font-size: 0.85rem;
    line-height: 1.3;
  }

  .schedule-duration-highlight {
    font-size: 0.72rem;
    padding: 0.18rem 0.5rem;
  }

  .timezone-field {
    gap: 0.25rem;
  }
}

/* Extra-compact tier for very small / older phones (≤400px) */
/* Small iPhones (iPhone SE) — aggressive trim so the calendar + summary
   don't push into the footer. Samsung and larger phones stay at the ≤640
   values set above. */
@media (max-width: 400px) {
  .campaign-scheduler {
    gap: 5px;
  }

  .scheduler-header h3 {
    font-size: clamp(0.95rem, 5vw, 1.15rem);
    margin-bottom: 2px;
  }

  .scheduler-body {
    gap: 6px;
  }

  .schedule-visual-stack {
    gap: 6px;
  }

  .action-stack {
    gap: 5px;
  }

  .schedule-btn {
    padding: 6px 8px;
    border-radius: 12px;
  }

  .schedule-btn__label {
    font-size: 0.62rem;
  }

  .schedule-btn__value {
    font-size: 0.74rem;
  }

  .calendar-card {
    padding: 0.4rem 0.5rem;
    border-radius: 16px;
  }

  .calendar-card__header {
    margin-bottom: 3px;
    gap: 4px;
  }

  .calendar-card__header h4 {
    font-size: 0.86rem;
  }

  .calendar-weekdays {
    margin-bottom: 2px;
    gap: 2px;
  }

  .calendar-weekdays span {
    font-size: 0.55rem;
  }

  .calendar-grid {
    gap: 1px 0;
  }

  .calendar-day {
    min-height: 17px;
    font-size: 0.64rem;
  }

  .calendar-day.is-start span,
  .calendar-day.is-end span {
    width: 17px;
    height: 17px;
  }

  .schedule-summary-card {
    padding: 0.45rem 0.55rem;
    gap: 4px;
    border-radius: 16px;
  }

  .schedule-summary-copy {
    font-size: 0.72rem;
    line-height: 1.25;
  }

  .schedule-duration-highlight {
    font-size: 0.64rem;
    padding: 0.12rem 0.42rem;
  }
}

/* Dual-month scheduler needs more horizontal room than the single-month
   default. Placed last so it overrides earlier viewport-specific rules
   that constrain `.scheduler-body` width. */
@media (min-width: 768px) and (min-height: 821px) {
  .campaign-scheduler .scheduler-body {
    max-width: min(100%, 62rem);
  }
}

@media (min-width: 1280px) and (min-height: 900px) {
  .campaign-scheduler .scheduler-body {
    max-width: min(100%, 72rem);
  }
}

@media (min-width: 768px) and (min-height: 821px) {
  .campaign-scheduler .calendar-card-pair .calendar-card {
    padding: 0.95rem 1.1rem;
  }
  .campaign-scheduler .calendar-card-pair .calendar-card__header h4 {
    font-size: 1.1rem;
  }
  .campaign-scheduler .calendar-card-pair .calendar-day {
    min-height: 32px;
    font-size: 0.95rem;
  }
}
</style>
